/* eslint-disable valid-jsdoc */
/* eslint-disable no-unreachable */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * MenuUploaderV2Service - Servicio para integración con backend AI
 * Conecta MenuUploaderV2 con Firebase Cloud Functions + Gemini directo
 */

import {
  AIAnalysisResult,
  DetectedCategory,
  DetectedElements,
  DetectedExtra,
  DetectedProduct,
  DetectedSection,
} from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';
import { storage, processMenuV2, processMenuV4, getMenuJobResult } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateJobId, isRecoverableCallableError, pollJobResult } from './menuJobRecovery';

// ====== TYPES PARA BACKEND INTEGRATION ======

// Estructura que devuelve el AI del backend
export interface BackendAIResult {
  overallConfidence: number;
  processingTime?: number;
  categories: Array<{
    id: string;
    nombre: string;
    confidence: number;
    originalText: string;
    orden?: number;
  }>;
  products: Array<{
    id: string;
    nombre: string;
    descripcion: string;
    confidence: number;
    originalText: string;
    categoryId: string;
    opciones: Array<{
      presentacion: string;
      precio: number;
      costo_produccion: number;
      confidence: number;
    }>;
  }>;
  extras: Array<{
    id: string;
    nombre: string;
    precio: number;
    costo_produccion: number;
    confidence: number;
    originalText: string;
    applicableToCategories: string[];
  }>;
  suggestions: string[];
  wasTruncated?: boolean; // Indica si Claude truncó la respuesta
  suspiciouslyLowCount?: boolean; // Ratio productos/página sospechosamente bajo
  tokenUsage?: {
    input: number;
    output: number;
  };
  metadata?: {
    stopReason?: string;
    inputTokens?: number;
    outputTokens?: number;
    wasTruncated?: boolean;
    suspiciouslyLowCount?: boolean;
    estimatedPages?: number;
    productsPerPage?: number;
  };
}

// ====== TYPES V4 (sub-fase 2) ======
// Schema V4 del backend prompt (espejo de menuV4.ts en plick-backend-firebase).
// La detección se hace por `schemaVersion === '4.0'` vía isV4Output().

export interface BackendV4Output {
  schemaVersion: '4.0';
  categories: Array<{
    kind: 'category';
    id: string;
    nombre: string;
    descripcion: string;
    estatus: 'activo' | 'inactivo';
    available_for: 'todos' | 'mesa' | 'recoger' | 'domicilio';
    disponibilidad_por_dias: number[];
    disponibilidad_por_horas: { inicio: string; fin: string };
    confidence: { score: number; reason: string };
  }>;
  products: Array<{
    kind: 'product';
    id: string;
    categoryId: string;
    nombre: string;
    descripcion: string;
    imagen: string;
    opciones: Array<{
      presentacion: string;
      precio: number;
      costo_produccion: number;
      estatus: 'activo' | 'inactivo';
    }>;
    sectionIds: string[];
    confidence: { score: number; reason: string };
  }>;
  extras: Array<{
    kind: 'extra';
    id: string;
    nombre: string;
    precio: number;
    costo_produccion: number;
    estatus: 'activo' | 'inactivo';
    appliesTo: { categoryIds: string[]; productIds: string[]; sectionIds: string[] };
    confidence: { score: number; reason: string };
  }>;
  sections: Array<{
    kind: 'section';
    id: string;
    nombre: string;
    numero_de_opciones: number;
    tipo: 'selec' | 'cont';
    obligatorio: boolean;
    es_multiplo: boolean;
    opciones: string[];
    applicableToCategories: string[];
    applicableToProducts: string[];
    confidence: { score: number; reason: string };
  }>;
  meta: {
    sourceLanguage: 'es-MX';
    extractedAt: string;
    notes: string[];
  };
}

export type BackendOutput = BackendAIResult | BackendV4Output;

/**
 * Type guard: el backend V4 marca su output con `schemaVersion: '4.0'`.
 * El legacy V3 no incluye ese campo, así que es discriminador suficiente.
 */
export function isV4Output(x: unknown): x is BackendV4Output {
  return typeof x === 'object' && x !== null && (x as { schemaVersion?: string }).schemaVersion === '4.0';
}

// Firebase Cloud Function Response Type
export interface FirebaseV2Response {
  data: {
    success: boolean;
    data?: BackendAIResult | BackendV4Output;
    error?: string;
    message?: string; // Mensaje descriptivo del backend
  };
}

export interface BackendAIModel {
  id: string;
  name: string;
  type: 'free' | 'premium' | 'balanced' | 'economical';
  category: 'gratuito' | 'pago';
  costPer1KTokens: number;
  contextTokens: number;
  description: string;
  recommended: string;
  strengths: string[];
}

export interface BackendModelsResponse {
  success: boolean;
  data: {
    models: BackendAIModel[];
    totalModels: number;
    freeModels: number;
    paidModels: number;
    recommendation: string;
  };
}

export interface BackendUploadResponse {
  success: boolean;
  message?: string;
  data: {
    jobId: string;
    fileUrl: string;
    fileName: string;
    fileSize: number;
    status: string;
    message: string;
  };
}

export interface BackendProcessResponse {
  success: boolean;
  message?: string;
  data: {
    jobId: string;
    fileName: string;
    model: string;
    result: BackendAIResult;
    metadata: {
      processingTime: number;
      cost: number;
      categoriesDetected: number;
      productsDetected: number;
      confidence: number;
    };
    message: string;
  };
}

export interface BackendJobStatusResponse {
  success: boolean;
  data: {
    jobId: string;
    status: 'uploaded' | 'processing' | 'completed' | 'failed';
    fileName: string;
    fileSize: number;
    aiModel: string;
    result: BackendAIResult | null;
    confidence: number;
    processingTime: number;
    cost: number;
    categoriesDetected: number;
    productsDetected: number;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
    metrics: Record<string, unknown>;
  };
}

export interface ProcessingOptions {
  model?: string;
  maxTokens?: number;
  temperature?: number;
  useUltraExhaustivePrompt?: boolean; // Para menús grandes y complejos
}

// ====== SERVICIO PRINCIPAL ======

class MenuUploaderV2Service {
  // MIGRADO A FIREBASE: Ya no necesitamos URL de Express
  // private readonly baseUrl = 'DEPRECATED - Using Firebase Cloud Functions now';

  /**
   * Verificar estado del backend Firebase
   */
  async healthCheck(): Promise<boolean> {
    try {
      // Firebase está siempre disponible
      return true;
    } catch (_error) {
      // console.error('Firebase health check failed:', _error);
      return false;
    }
  }

  /**
   * Obtener modelos Gemini disponibles
   */
  async getAvailableModels(): Promise<BackendAIModel[]> {
    try {
      // Firebase usa Gemini directo - retornamos modelos disponibles
      const geminiModels: BackendAIModel[] = [
        {
          id: 'gemini-2.5-flash',
          name: 'Gemini 1.5 Pro',
          type: 'premium',
          category: 'pago',
          costPer1KTokens: 0.00625, // $1.25 + $5.00 average
          contextTokens: 2000000,
          description: 'Modelo premium de máxima calidad para análisis completo de PDFs complejos',
          recommended: '🏆 RECOMENDADO - Máxima precisión (95-98%) + Sin límites diarios',
          strengths: ['maximum-quality', 'large-context-2M', 'no-daily-limits', 'complex-pdfs'],
        },
        {
          id: 'gemini-1.5-flash',
          name: 'Gemini 1.5 Flash',
          type: 'balanced',
          category: 'pago',
          costPer1KTokens: 0.000375, // $0.075 + $0.30 average
          contextTokens: 1000000,
          description: 'Balance entre velocidad y calidad para menús simples',
          recommended: 'Para menús básicos y testing rápido',
          strengths: ['fast', 'cost-effective', 'reliable', 'no-limits'],
        },
      ];

      return geminiModels;
    } catch (error) {
      // console.error('Error fetching Gemini models:', error);
      throw new Error('No se pudieron cargar los modelos Gemini disponibles');
    }
  }

  /**
   * Subir archivo a Firebase Storage (solo upload, sin procesamiento)
   * @param file - Archivo a subir
   * @returns Objeto con jobId y URL del archivo subido
   */
  async uploadFile(file: File): Promise<{ jobId: string; fileUrl: string }> {
    try {
      // Firebase no usa jobs como Express, pero mantenemos interfaz compatible
      const jobId = `firebase-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Subir a Firebase Storage
      const storageRef = ref(storage, `menus/v2/${jobId}-${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // console.log('✅ Archivo subido a Firebase Storage:', { jobId, fileUrl });

      return { jobId, fileUrl };
    } catch (error) {
      // console.error('Error uploading file to Firebase:', error);
      throw new Error(error instanceof Error ? error.message : 'Error al subir el archivo a Firebase');
    }
  }

  /**
   * Procesar archivo ya subido con Firebase
   * @param jobId - ID del trabajo (no usado en Firebase)
   * @param options - Opciones de procesamiento (no usado en Firebase)
   * @returns Resultado del procesamiento
   */
  async processUploadedFile(jobId: string, options: ProcessingOptions = {}): Promise<BackendAIResult> {
    // Evitar warning de parámetros no usados
    // Referencias a parámetros para evitar warnings (ahora innecesario con eslint-disable)
    try {
      // En Firebase no hay separación upload/process - redirects to uploadAndProcessDirect
      // console.log('⚠️ processUploadedFile: Firebase procesa directamente, necesitas usar uploadAndProcessDirect');

      // Para mantener compatibilidad, retornamos estructura básica
      throw new Error(
        'Firebase no soporta procesamiento separado. Usa uploadAndProcessDirect con el archivo original.',
      );
    } catch (error) {
      // console.error('Error in Firebase separated processing:', error);
      throw new Error(
        error instanceof Error ? error.message : 'Error al procesar el archivo separadamente con Firebase',
      );
    }
  }

  /**
   * Subir N archivos a Firebase Storage en paralelo y procesarlos en UNA sola
   * invocación de `processMenuV4` con `fileUrls: string[]`.
   *
   * Por qué single-call: el agente Claude del backend ve todo el menú a la vez,
   * lo que permite consolidación cross-archivo nativa (sin dedup post-hoc en el
   * frontend) y reduce el costo de prompt N× a 1×.
   *
   * Validación: `files.length` debe estar entre 1 y 15 (límite del prompt V4).
   *
   * @param {File[]} files Lista de archivos del menú (PDF/imagen). 1-15.
   * @param {{ onUploadsComplete?: () => void; onJobStart?: Function }} [opts]
   *   `onUploadsComplete` se invoca cuando TODOS los archivos terminaron de subir
   *   a Storage y está por arrancar el procesamiento LLM (la UI distingue la fase
   *   corta de subida de la fase larga de análisis). `onJobStart` recibe
   *   `{jobId, fileUrls}` justo antes de invocar el callable — el caller puede
   *   persistirlos para recuperar el resultado tras un reload de página
   *   (ver `recoverJobResult`).
   * @returns {Promise<BackendAIResult>} Cast legacy; el caller debe usar
   *   `isV4Output(result)` para discriminar V4 vs V3 antes de transformar.
   */
  async uploadBatchAndProcess(
    files: File[],
    opts: {
      onUploadsComplete?: () => void;
      onJobStart?: (info: { jobId: string; fileUrls: string[] }) => void;
    } = {},
  ): Promise<BackendAIResult> {
    if (files.length < 1 || files.length > 15) {
      throw new Error(`uploadBatchAndProcess espera entre 1 y 15 archivos, recibió ${files.length}.`);
    }

    try {
      // 1. Subir en paralelo a Firebase Storage. El timestamp + índice + nombre
      //    garantizan unicidad sin necesidad de UUID por archivo.
      const batchTs = Date.now();
      const fileUrls = await Promise.all(
        files.map(async (file, idx) => {
          const storageRef = ref(storage, `menus/v4/${batchTs}-${idx}-${file.name}`);
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        }),
      );

      // Subida completa → arranca la fase LLM. El callback nunca debe romper
      // el flujo: cualquier error del listener se traga silenciosamente.
      try {
        opts.onUploadsComplete?.();
      } catch {
        /* listener de UI; no afecta el procesamiento */
      }

      // 2. UNA invocación al callable V4 con todos los URLs. `fileTypes` paralelo
      //    al array `fileUrls`; backend usa default 'application/pdf' si se omite,
      //    pero lo enviamos explícito por correctness con imágenes mixtas.
      //    `jobId` permite recuperar el resultado persistido en Storage si la
      //    conexión del callable muere mid-flight (NAT/proxy idle, deploy).
      const jobId = generateJobId();
      // Notificar al caller ANTES del callable: si la página se recarga durante
      // el análisis, el caller ya persistió {jobId, fileUrls} y puede retomar
      // con recoverJobResult. Mismo contrato fail-safe que onUploadsComplete.
      try {
        opts.onJobStart?.({ jobId, fileUrls });
      } catch {
        /* listener de UI; no afecta el procesamiento */
      }
      let result: FirebaseV2Response;
      try {
        result = (await processMenuV4({
          fileUrls,
          fileTypes: files.map((f) => f.type),
          jobId,
        })) as FirebaseV2Response;
      } catch (callableError) {
        if (!isRecoverableCallableError(callableError)) {
          throw callableError; // Error de negocio: no hay nada que recuperar.
        }
        // eslint-disable-next-line no-console
        console.warn(
          `[uploadBatchAndProcess] Transporte del callable V4 murió; entrando a recovery con jobId=${jobId}`,
        );
        let recovered: unknown;
        try {
          recovered = await pollJobResult(async (id) => {
            // El callable envuelve su respuesta en {data: ...}; desempacamos aquí
            // para que pollJobResult vea el contrato plano {found, pending, result}.
            const res = (await getMenuJobResult({ jobId: id })) as {
              data: { found: boolean; pending?: boolean; result?: unknown };
            };
            return res.data;
          }, jobId);
        } catch (recoveryError) {
          if (recoveryError instanceof Error && recoveryError.message === 'JOB_RECOVERY_TIMEOUT') {
            throw new Error(
              'El procesamiento sigue en curso pero no pudimos recuperar el resultado. Intenta de nuevo en unos minutos.',
            );
          }
          // JOB_NOT_FOUND: el backend nunca recibió/arrancó el job → el error
          // original del callable es el diagnóstico real para el usuario.
          throw callableError;
        }
        // El resultado persistido es el payload interno {success, data|error, message}
        // (lo que el callable entregaría en `.data`). Re-envolvemos en {data} para que
        // la validación downstream (paso 3) sea idéntica en ambos caminos.
        result = { data: recovered as FirebaseV2Response['data'] };
      }

      // 3. Validar shape de respuesta. Mismo contrato que el flujo single-file.
      if (!result.data?.success || !result.data?.data) {
        throw new Error(result.data?.error || 'Claude processing failed');
      }

      // 4. Devolver el payload tal cual. El caller decide V4 vs V3 vía
      //    `isV4Output()` (V4 trae schemaVersion '4.0'; V3 no). El cast a
      //    BackendAIResult es legacy y se mantiene por compat de firma con
      //    `uploadAndProcessDirect`. NO transformamos aquí — la decisión
      //    V4 vs V3 vive en el caller (hook o page).
      return result.data.data as BackendAIResult;
    } catch (error) {
      // Re-lanzar el error ORIGINAL: envolverlo en `new Error(message)` borra
      // `.code`/`.isAxiosError`/`.response`, que `classifyError` (hook) usa para
      // distinguir network/timeout/quota/file_invalid (Seer 3393545368).
      if (error instanceof Error) throw error;
      throw new Error('Error al procesar el menú con Claude');
    }
  }

  /**
   * Recupera el resultado de un job V4 ya lanzado, por jobId — el camino de
   * resume tras un reload de página. Hace polling a `getMenuJobResult` (el
   * backend persiste el resultado bajo el jobId) y aplica la MISMA validación
   * de shape que `uploadBatchAndProcess`, devolviendo el payload idéntico.
   *
   * Errores esperables:
   * - `JOB_NOT_FOUND` (del poll): el backend no conoce ese job — el caller debe
   *   limpiar la referencia persistida y ofrecer empezar de cero.
   * - Timeout de recovery: mismo mensaje humano que el camino in-flight.
   *
   * @param {string} jobId Id persistido al arrancar el batch (ver `onJobStart`).
   * @returns {Promise<BackendAIResult>} Payload del backend (V4 o V3; cast legacy).
   */
  async recoverJobResult(jobId: string): Promise<BackendAIResult> {
    let recovered: unknown;
    try {
      recovered = await pollJobResult(async (id) => {
        const res = (await getMenuJobResult({ jobId: id })) as {
          data: { found: boolean; pending?: boolean; result?: unknown };
        };
        return res.data;
      }, jobId);
    } catch (recoveryError) {
      if (recoveryError instanceof Error && recoveryError.message === 'JOB_RECOVERY_TIMEOUT') {
        throw new Error(
          'El procesamiento sigue en curso pero no pudimos recuperar el resultado. Intenta de nuevo en unos minutos.',
        );
      }
      throw recoveryError;
    }
    const result: FirebaseV2Response = { data: recovered as FirebaseV2Response['data'] };
    if (!result.data?.success || !result.data?.data) {
      throw new Error(result.data?.error || 'Claude processing failed');
    }
    return result.data.data as BackendAIResult;
  }

  /**
   * Subir y procesar UN archivo en una sola llamada con Firebase.
   *
   * @deprecated Usar `uploadBatchAndProcess([file])`. Esta firma queda como
   * wrapper temporal para callers legacy (`useMenuProcessing.ts`) que aún esperan
   * la API single-file. El nuevo flujo Smart Menu Onboarding va por batch.
   *
   * @param file - Archivo a procesar
   * @param _options - Opciones de procesamiento (no usadas; V4 decide internamente)
   * @returns Resultado del procesamiento con IA
   */
  async uploadAndProcessDirect(file: File, _options: ProcessingOptions = {}): Promise<BackendAIResult> {
    return this.uploadBatchAndProcess([file]);
  }

  /**
   * Obtener estado de un job de procesamiento (Firebase simplificado)
   * @param jobId - ID del trabajo a consultar
   * @returns Estado del trabajo de procesamiento
   */
  async getJobStatus(jobId: string): Promise<BackendJobStatusResponse['data']> {
    try {
      // Firebase procesa inmediatamente, pero mantenemos interfaz compatible
      return {
        jobId,
        status: 'completed' as const,
        fileName: 'processed-file',
        fileSize: 0,
        aiModel: 'gemini-2.5-flash',
        result: null, // Se obtiene directamente en uploadAndProcessDirect
        confidence: 85,
        processingTime: 0,
        cost: 0.15,
        categoriesDetected: 0,
        productsDetected: 0,
        errorMessage: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metrics: {},
      };
    } catch (error) {
      // console.error('Error getting Firebase job status:', error);
      throw new Error(error instanceof Error ? error.message : 'Error al obtener estado del procesamiento Firebase');
    }
  }

  /**
   * Transformar respuesta del backend al formato esperado por el frontend
   * @param backendResult - Resultado del backend Firebase
   * @param originalFile - Archivo original procesado
   * @returns Resultado transformado al formato frontend
   */
  transformBackendToFrontend(backendResult: BackendAIResult, originalFile: File): AIAnalysisResult {
    const detectedElements: DetectedElements = {
      categories: backendResult.categories.map((cat) => ({
        id: cat.id,
        name: cat.nombre.replace(/\s+\d+\s*$/, '').trim(), // Limpiar números sueltos
        confidence: cat.confidence,
        originalText: cat.originalText,
        type: 'category' as const,
        products: backendResult.products.filter((prod) => prod.categoryId === cat.id).map((prod) => prod.id),
      })),
      products: backendResult.products.flatMap((prod) => {
        // Si tiene múltiples opciones, crear un producto por opción
        if (prod.opciones && prod.opciones.length > 1) {
          return prod.opciones.map((opcion, index) => ({
            id: `${prod.id}-${index}`,
            name: `${prod.nombre.replace(/\s+\d+\s*$/, '').trim()} (${opcion.presentacion})`, // Limpiar nombre base
            confidence: Math.min(prod.confidence, opcion.confidence),
            originalText: `${prod.originalText} - ${opcion.presentacion}`,
            type: 'product' as const,
            price: opcion.precio,
            description: `${prod.descripcion} - ${opcion.presentacion}`,
            category: prod.categoryId,
          }));
        }
        // Producto con una sola opción
        const opcion = prod.opciones?.[0];
        return [
          {
            id: prod.id,
            name: prod.nombre.replace(/\s+\d+\s*$/, '').trim(), // Limpiar números sueltos al final
            confidence: prod.confidence,
            originalText: prod.originalText,
            type: 'product' as const,
            price: opcion?.precio,
            description: prod.descripcion,
            category: prod.categoryId,
          },
        ];
      }),
      extras: backendResult.extras.map((extra) => ({
        id: extra.id,
        name: extra.nombre.replace(/\s+\d+\s*$/, '').trim(), // Limpiar números sueltos
        confidence: extra.confidence,
        originalText: extra.originalText,
        type: 'extra' as const,
        price: extra.precio,
        applicableProducts: backendResult.products
          .filter((prod) => extra.applicableToCategories.includes(prod.categoryId))
          .map((prod) => prod.id),
      })),
    };

    return {
      overallConfidence: backendResult.overallConfidence,
      detectedElements,
      originalFile,
      processingTime: backendResult.processingTime || 0,
      suggestions: backendResult.suggestions || [],
    };
  }

  /**
   * Polling para jobs de larga duración
   * @param jobId - ID del trabajo a consultar
   * @param onUpdate - Callback para actualizar estado
   * @param intervalMs - Intervalo entre consultas en ms
   * @param maxAttempts - Número máximo de intentos
   * @returns Estado final del trabajo
   */
  async pollJobStatus(
    jobId: string,
    onUpdate?: (status: BackendJobStatusResponse['data']) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 150, // 5 minutos máximo
  ): Promise<BackendJobStatusResponse['data']> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const status = await this.getJobStatus(jobId);

        // Llamar callback si se proporciona
        if (onUpdate) {
          onUpdate(status);
        }

        // Si terminó (exitoso o error), retornar
        if (status.status === 'completed' || status.status === 'failed') {
          return status;
        }

        // Esperar antes del siguiente intento
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        attempts++;
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          throw error;
        }
        // Esperar antes de reintentar en caso de error
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error('Timeout: El procesamiento tomó demasiado tiempo');
  }
}

// ====== TRANSFORMER V4 (sub-fase 2) ======

/**
 * Transforma el output V4 del backend al shape AIAnalysisResult del frontend.
 *
 * Diferencias clave vs `transformBackendToFrontend` (V3):
 * - NO aplana `opciones[]`: un product V4 con N presentaciones produce UN
 *   `DetectedProduct` con su array `opciones` intacto. El persist (sub-fase 3)
 *   usa esas opciones literales en lugar de generar `{presentacion:'pza'}`.
 * - Preserva refs cross-entity ricas: `sectionIds` en products y `appliesTo`
 *   en extras pasan tal cual para que el persist remapee IDs sintéticos
 *   `cat_/prod_/sec_/ext_` a IDs reales tras crear cada entidad.
 * - Mapea sections V4 a `DetectedSection` (interface nueva sub-fase 1).
 *
 * Caveats:
 * - `available_for` vive en CategoryOutV4 (V4) pero el frontend lo expone hoy
 *   solo a nivel producto (no en DetectedCategory). Decisión: NO propagarlo
 *   por producto en esta sub-fase para evitar agregar campos a DetectedProduct
 *   sin uso real; la categoría persiste su `available_for` directamente en el
 *   POST /v1/categoria (sub-fase 3). Si en el futuro el frontend lo necesita
 *   por-producto (override), se añade aquí desde `cat.available_for`.
 * - `es_multiplo` cambia de boolean (V4) a 'simple'|'multiple' (frontend canónico).
 * - `confidence` se reduce de {score, reason} → number (score). El `reason` se
 *   pierde aquí; en sub-fase 4 lo expondremos vía meta.notes o tooltip.
 * - `originalText` no existe en V4; usamos `nombre` como fallback razonable.
 */
export const transformV4toFrontend = (v4: BackendV4Output, originalFile: File): AIAnalysisResult => {
  // Categories: 1:1 + lookup de products por categoryId
  const categories: DetectedCategory[] = v4.categories.map((cat) => ({
    id: cat.id,
    name: cat.nombre,
    confidence: cat.confidence.score,
    originalText: cat.nombre,
    type: 'category' as const,
    products: v4.products.filter((p) => p.categoryId === cat.id).map((p) => p.id),
  }));

  // Products: 1:1 conservando opciones[] y sectionIds
  // CRÍTICO: NO flatMap por opción. Cada producto V4 = un DetectedProduct.
  const products: DetectedProduct[] = v4.products.map((prod) => ({
    id: prod.id,
    name: prod.nombre,
    confidence: prod.confidence.score,
    originalText: prod.nombre,
    type: 'product' as const,
    description: prod.descripcion,
    category: prod.categoryId,
    // price headline = primera opción (lo que el frontend muestra en cards)
    price: prod.opciones[0]?.precio,
    opciones: prod.opciones,
    sectionIds: prod.sectionIds,
  }));

  // Extras: 1:1 preservando appliesTo (sub-fase 3 lo usa para binding via sections)
  const extras: DetectedExtra[] = v4.extras.map((extra) => ({
    id: extra.id,
    name: extra.nombre,
    confidence: extra.confidence.score,
    originalText: extra.nombre,
    type: 'extra' as const,
    price: extra.precio,
    appliesTo: extra.appliesTo,
  }));

  // Sections: nueva entidad V4. Mapeo de boolean es_multiplo → enum canónico.
  const sections: DetectedSection[] = v4.sections.map((sec) => ({
    id: sec.id,
    name: sec.nombre,
    confidence: sec.confidence.score,
    originalText: sec.nombre,
    type: 'section' as const,
    numero_de_opciones: sec.numero_de_opciones,
    tipo: sec.tipo,
    obligatorio: sec.obligatorio,
    es_multiplo: sec.es_multiplo ? 'multiple' : 'simple',
    opciones: sec.opciones,
    applicableToCategories: sec.applicableToCategories,
    applicableToProducts: sec.applicableToProducts,
  }));

  const detectedElements: DetectedElements = {
    categories,
    products,
    extras,
    sections,
  };

  // overallConfidence: promedio simple de todas las confidences. V4 las trae
  // por entidad (0-100); si no hay nada, devolvemos 0.
  const allScores: number[] = [
    ...v4.categories.map((c) => c.confidence.score),
    ...v4.products.map((p) => p.confidence.score),
    ...v4.extras.map((e) => e.confidence.score),
    ...v4.sections.map((s) => s.confidence.score),
  ];
  const overallConfidence =
    allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;

  return {
    overallConfidence,
    detectedElements,
    originalFile,
    processingTime: 0, // V4 no expone tiempo; lo medirá el caller si lo necesita
    suggestions: v4.meta.notes,
  };
};

// Export singleton instance
export const menuUploaderV2Service = new MenuUploaderV2Service();
export default menuUploaderV2Service;
